// Phase HUB-DRILL-1 — slide-over drawer wrapping MainHubDetailView.
// Triggered from MainHubsList and CEODashboard table.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import useTimedFetch from "../../hooks/useTimedFetch";
import MainHubDetailView, { HubDetailLoading, HubDetailError } from "./MainHubDetailView";

export default function HubDetailDrawer({ hubId, open, onClose }) {
  const [filters, setFilters] = useState({});
  // Build params for the backend from active filters
  const params = {};
  Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });

  const { data, loading, error, reload } = useTimedFetch(
    hubId ? `/founder/main-hubs/${hubId}` : null,
    { params, skip: !open || !hubId });

  // Reset filters whenever a different hub is opened.
  useEffect(() => { setFilters({}); }, [hubId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="hub-detail-drawer" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true"/>
      <aside className="relative ml-auto w-full max-w-3xl h-full bg-slate-50 shadow-2xl flex flex-col">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 shrink-0">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Main hub</span>
          <span className="text-sm font-mono text-slate-700 ml-1">{hubId}</span>
          <button onClick={onClose} aria-label="Close"
                  data-testid="hub-detail-drawer-close"
                  className="ml-auto p-1.5 rounded-full hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00]">
            <X className="h-5 w-5"/>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && !data && <HubDetailLoading/>}
          {error && <HubDetailError {...error} onRetry={reload}/>}
          {data && (
            <MainHubDetailView
              data={data}
              hubId={hubId}
              reload={reload}
              filters={filters}
              onFiltersChange={setFilters}
              variant="drawer"
            />
          )}
        </div>
      </aside>
    </div>
  );
}
